import React, { useState, useEffect, useRef, useContext } from "react";
import { cn } from "../../lib/helpers";
import { SectionContext } from "./context";
import { SectionHeaders } from "./wrapper";
import { ImageBuilder } from "../global/image-builder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas as iconSet } from "@fortawesome/free-solid-svg-icons";
import * as styles from "./column-pin-stack.module.css";

const PinCardBack = ({ column, isDarkBg }) => (
  <div className={styles.pinCardBack}>
    {column.icon && (
      <FontAwesomeIcon
        icon={iconSet[column.icon]}
        size="2x"
        className={cn("max-h-16", isDarkBg ? "text-white" : "text-green-emerald")}
      />
    )}
    {column.mainImage && !column.icon && (
      <ImageBuilder image={column.mainImage} width={48} alt={column.header || "icon"} />
    )}
    {column.header && (
      <h4 className="text-4xl font-semibold">{column.header}</h4>
    )}
  </div>
);

export const PinStack = ({ section, styleClass, gapClass, specialClass, getItemClassName, renderItem }) => {
  const sectionContext = useContext(SectionContext);
  const [pinVisibleCount, setPinVisibleCount] = useState(0);
  const outerRef = useRef(null);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const scrolled = Math.max(0, -rect.top);
      const room = el.offsetHeight - window.innerHeight;
      if (room <= 0) { setPinVisibleCount(section.content.length); return; }
      setPinVisibleCount(Math.ceil((scrolled / room) * section.content.length));
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [section.content?.length]);

  return (
    <div
      ref={outerRef}
      style={{ minHeight: `calc(100vh + ${section.content.length * 500}px)` }}
    >
      <div className="sticky top-16 py-12">
        <SectionHeaders {...section} isDarkBg={sectionContext.isDarkBg} />
        <div className={cn(gapClass, styleClass, specialClass, section.style === "overlap" ? "xl:pt-24" : null)}>
          {section.content.map((column, i) => (
            <div
              key={i}
              className={cn(
                getItemClassName(column, i),
                styles.pinItem,
                pinVisibleCount > i ? styles.pinItemVisible : null,
                pinVisibleCount - 1 === i ? `ring-2 ${section.anchor === "broken" ? "ring-red" : "ring-link"} rounded-lg` : null,
              )}
            >
              <PinCardBack column={column} isDarkBg={sectionContext.isDarkBg} />
              <div className={styles.pinCardFront}>
                {renderItem(column, i)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
